/**
 * Onboarding API Service
 * Handles employee onboarding wizard API calls
 */

import { apiGet, apiPost, apiDelete, apiPut, API_BASE_URL } from '@/shared/api/apiClient';
import { STORAGE_KEYS } from '@/config/appConfig';
import { getToken } from '@/services/authService';

// Gender, MaritalStatus and AccountType are no longer frontend enums — they are
// rows in the backend's common.system_codes table, fetched via
// useSystemCodes('Gender' | 'MaritalStatus' | 'AccountType'). DTOs below carry
// the selected system code's RowId (`...Id`, a GUID) plus its resolved label
// (`...Value`) instead of a numeric/string enum field.

// ===== ENUMS =====

export enum OnboardingStatus {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Submitted = "Submitted",
  UnderReview = "UnderReview",
  RevisionRequested = "RevisionRequested",
  Completed = "Completed",
  Rejected = "Rejected"
}

export enum DocumentType {
  Aadhaar = 1,
  PAN = 2,
  Photo = 3,
  EducationCertificate = 4,
  ExperienceLetter = 5,
  RelievingLetter = 6,
  AddressProof = 7,
  BankPassbook = 8,
  CancelledCheque = 9,
  Other = 99
}

export enum ReviewAction {
  Approve = 1,
  RequestRevision = 2,
  Reject = 3
}

// ===== DTOs =====

export interface OnboardingPersonalInfoDto {
  id: string;
  onboardingRecordId: string;
  dateOfBirth: string;
  genderId: string;
  genderValue: string;
  bloodGroup?: string;
  nationality?: string;
  permanentAddress: string;
  currentAddress: string;
  isSameAddress: boolean;
  personalEmail: string;
  phoneNumber: string;
  maritalStatusId: string;
  maritalStatusValue: string;
  joiningDate?: string;
}

export interface OnboardingDocumentDto {
  id: string;
  onboardingRecordId: string;
  documentType: DocumentType;
  documentName: string;
  filePath: string;
  viewUrl?: string;
  fileSize: number;
  mimeType: string;
  isVerified: boolean;
  uploadedAt: string;
}

export interface OnboardingPolicyAgreementDto {
  id: string;
  onboardingRecordId: string;
  policyId?: string;
  policyTitle: string;
  isAgreed: boolean;
  agreedAt?: string;
  ipAddress?: string;
  remarks?: string;
}

export interface OnboardingEmergencyContactDto {
  id: string;
  onboardingRecordId: string;
  contactName: string;
  relationship: string;
  phoneNumber: string;
  alternatePhone?: string;
  email?: string;
  address?: string;
  isPrimary: boolean;
}

export interface OnboardingBankDetailsDto {
  id: string;
  onboardingRecordId: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  branchName?: string;
  accountTypeId: string;
  accountTypeValue: string;
  isPrimary: boolean;
  aadharNumber?: string;
  panNumber?: string;
}

export interface OnboardingNdaSignatureDto {
  id: string;
  ndaDocumentTitle: string;
  ndaDocumentContent: string;
  signatoryFullName: string;
  signatoryEmail: string;
  isSigned: boolean;
  signedAt?: string;
  digitalSignatureHash?: string;
}

export interface NdaContentDto {
  title: string;
  content: string;
}

export interface OnboardingStatusDto {
  id: string;
  employeeId: string;
  employeeName?: string;
  status: OnboardingStatus;
  currentStep: number;
  startedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewerNote?: string;
  personalInfo?: OnboardingPersonalInfoDto;
  documents: OnboardingDocumentDto[];
  policyAgreements: OnboardingPolicyAgreementDto[];
  emergencyContacts: OnboardingEmergencyContactDto[];
  bankDetails?: OnboardingBankDetailsDto;
  ndaSignature?: OnboardingNdaSignatureDto;
  /**
   * False for tenants with no NDA of their own (Onboarding:NdaDisabledTenants on the API).
   * The wizard hides the NDA step when this is false. Optional so an older API that does not
   * send it still behaves as before — treat `undefined` as required.
   */
  ndaRequired?: boolean;
}

export interface OnboardingListItemDto {
  id: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  email: string;
  departmentName?: string;
  joiningDate: string;
  status: OnboardingStatus;
  currentStep: number;
  startedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewerName?: string;
}

export interface PolicyDto {
  id: string;
  title: string;
  description: string;
  documentUrl?: string;
  isRequired?: boolean;
}

// ===== REQUEST DTOs =====

export interface SavePersonalInfoRequest {
  onboardingRecordId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  genderId: string;
  bloodGroup?: string;
  nationality?: string;
  permanentAddress: string;
  currentAddress: string;
  isSameAddress: boolean;
  personalEmail: string;
  phoneNumber: string;
  maritalStatusId: string;
  joiningDate: string;
}

export interface SavePolicyAgreementRequest {
  onboardingRecordId: string;
  policyId?: string;
  policyTitle: string;
  isAgreed: boolean;
  ipAddress?: string;
  remarks?: string;
}

export interface SaveEmergencyContactRequest {
  onboardingRecordId: string;
  id?: string;
  contactName: string;
  relationship: string;
  phoneNumber: string;
  alternatePhone?: string;
  email?: string;
  address?: string;
  isPrimary: boolean;
}

export interface SaveBankDetailsRequest {
  onboardingRecordId: string;
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  branchName?: string;
  accountTypeId: string;
  isPrimary: boolean;
  aadharNumber?: string;
  panNumber?: string;
}

export interface SubmitOnboardingRequest {
  onboardingRecordId: string;
}

export interface SignNdaRequest {
  onboardingRecordId: string;
  signatoryFullName: string;
  signatoryEmail: string;
}

export interface ReviewOnboardingRequest {
  onboardingRecordId: string;
  action: ReviewAction;
  note?: string;
}

export interface UpdateCurrentStepRequest {
  onboardingRecordId: string;
  step: number;
}

// ===== API FUNCTIONS =====

/**
 * Initiate onboarding for an employee
 */
export async function initiateOnboarding(employeeId: string): Promise<OnboardingStatusDto> {
  return apiPost<OnboardingStatusDto>('/onboarding/initiate', { employeeId });
}

/**
 * Get onboarding status with all details
 */
export async function getOnboardingStatus(employeeId: string): Promise<OnboardingStatusDto> {
  return apiGet<OnboardingStatusDto>(`/onboarding/status/${employeeId}`);
}

/**
 * Check if onboarding is complete
 */
export async function isOnboardingComplete(employeeId: string): Promise<boolean> {
  return apiGet<boolean>(`/onboarding/complete/${employeeId}`);
}

/**
 * True when the onboarding record needs the employee to do something in the wizard
 * (not started, in progress, or sent back for revision) — as opposed to submitted
 * and pending admin review, or already approved, in which case they belong on the
 * dashboard instead. Mirrors OnboardingWizardPage's own status handling.
 */
export function onboardingNeedsAction(status: OnboardingStatus): boolean {
  return (
    status === OnboardingStatus.NotStarted ||
    status === OnboardingStatus.InProgress ||
    status === OnboardingStatus.RevisionRequested
  );
}

/**
 * Save personal information (Step 1)
 */
export async function savePersonalInfo(request: SavePersonalInfoRequest): Promise<OnboardingPersonalInfoDto> {
  return apiPost<OnboardingPersonalInfoDto>('/onboarding/personal-info', request);
}

/**
 * Upload document (Step 2) — uses XHR to report upload progress.
 */
export function uploadDocument(
  onboardingRecordId: string,
  documentType: DocumentType,
  file: File,
  onProgress?: (percent: number) => void
): Promise<OnboardingDocumentDto> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('OnboardingRecordId', onboardingRecordId);
    formData.append('DocumentType', documentType.toString());
    formData.append('File', file);

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as OnboardingDocumentDto);
        } catch {
          reject(new Error('Invalid response from server'));
        }
      } else if (xhr.status === 401) {
        sessionStorage.removeItem(STORAGE_KEYS.token);
        sessionStorage.removeItem(STORAGE_KEYS.user);
        window.location.href = '/login';
        reject(new Error('Session expired. Please login again.'));
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.error || `HTTP ${xhr.status}: ${xhr.statusText}`));
        } catch {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    const token = getToken();
    const userJson = sessionStorage.getItem(STORAGE_KEYS.user);
    const tenantId = userJson ? (JSON.parse(userJson).tenantId as string | undefined) : null;
    const timezone = localStorage.getItem('user_timezone') ||
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    xhr.open('POST', `${API_BASE_URL}/onboarding/documents`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (tenantId) xhr.setRequestHeader('X-Tenant-Id', tenantId);
    xhr.setRequestHeader('X-Timezone', timezone);

    xhr.send(formData);
  });
}

/**
 * Delete document
 */
export async function deleteDocument(documentId: string): Promise<void> {
  return apiDelete<void>(`/onboarding/documents/${documentId}`);
}

/**
 * Verify or unverify a document (HR admin action)
 */
export async function verifyDocument(documentId: string, isVerified: boolean): Promise<void> {
  return apiPut<void>(`/onboarding/documents/${documentId}/verify`, { isVerified });
}

/**
 * Save policy agreement (Step 3)
 */
export async function savePolicyAgreement(request: SavePolicyAgreementRequest): Promise<OnboardingPolicyAgreementDto> {
  return apiPost<OnboardingPolicyAgreementDto>('/onboarding/policy-agreement', request);
}

/**
 * Save emergency contact (Step 4)
 */
export async function saveEmergencyContact(request: SaveEmergencyContactRequest): Promise<OnboardingEmergencyContactDto> {
  return apiPost<OnboardingEmergencyContactDto>('/onboarding/emergency-contact', request);
}

/**
 * Delete emergency contact
 */
export async function deleteEmergencyContact(contactId: string): Promise<void> {
  return apiDelete<void>(`/onboarding/emergency-contact/${contactId}`);
}

/**
 * Save bank details (Step 5)
 */
export async function saveBankDetails(request: SaveBankDetailsRequest): Promise<OnboardingBankDetailsDto> {
  return apiPost<OnboardingBankDetailsDto>('/onboarding/bank-details', request);
}

/**
 * Submit onboarding for review (Step 7)
 */
export async function submitOnboarding(request: SubmitOnboardingRequest): Promise<OnboardingStatusDto> {
  return apiPost<OnboardingStatusDto>('/onboarding/submit', request);
}

/**
 * Close the caller's own onboarding without filling it in. Admin only — the API rejects
 * anyone else with a 403, and takes the employee id from the token rather than the body.
 */
export async function skipOnboarding(): Promise<OnboardingStatusDto> {
  return apiPost<OnboardingStatusDto>('/onboarding/skip', {});
}

/**
 * Review onboarding (HR only)
 */
export async function reviewOnboarding(request: ReviewOnboardingRequest): Promise<OnboardingStatusDto> {
  return apiPost<OnboardingStatusDto>('/onboarding/review', request);
}

/**
 * Get onboarding list for HR dashboard
 */
export async function getOnboardingList(status?: OnboardingStatus): Promise<OnboardingListItemDto[]> {
  const queryParams = status ? `?status=${status}` : '';
  return apiGet<OnboardingListItemDto[]>(`/onboarding/list${queryParams}`);
}

/**
 * Get available policies
 */
export async function getPolicies(): Promise<PolicyDto[]> {
  return apiGet<PolicyDto[]>('/onboarding/policies');
}

/**
 * Update current step manually
 */
export async function updateCurrentStep(request: UpdateCurrentStepRequest): Promise<void> {
  return apiPost<void>('/onboarding/update-step', request);
}

/**
 * Get NDA content for signing (Step 7)
 */
export async function getNdaContent(): Promise<NdaContentDto> {
  return apiGet<NdaContentDto>('/onboarding/nda');
}

/**
 * Sign NDA digitally (Step 7)
 */
export async function signNda(request: SignNdaRequest): Promise<OnboardingNdaSignatureDto> {
  return apiPost<OnboardingNdaSignatureDto>('/onboarding/nda/sign', request);
}

// ===== React Query Keys =====

export const onboardingKeys = {
  all: ['onboarding'] as const,
  status: (employeeId: string) => [...onboardingKeys.all, 'status', employeeId] as const,
  list: () => [...onboardingKeys.all, 'list'] as const,
  policies: () => [...onboardingKeys.all, 'policies'] as const,
};
